import React, { useState, useEffect } from 'react';
import { Container, Button, Box, CircularProgress, Alert } from '@mui/material';
import CipherChallenge from '../components/CipherChallenge';
import { getRandomQuote, atbashEncode } from '../utils/cipherUtils';

const AtbashCipher = () => {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const generateNewChallenge = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quote = await getRandomQuote();
      if (quote) {
        setChallenge({
          encodedMessage: atbashEncode(quote.text),
          correctAnswer: quote.text,
          originalText: quote.text,
          hint: "Write out the alphabet and its reverse to help you decode. A ↔ Z, B ↔ Y, C ↔ X, etc.",
          difficulty: quote.difficulty
        });
      }
    } catch (error) {
      console.error('Error generating challenge:', error);
      setError('Failed to load challenge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateNewChallenge();
  }, []);

  const handleComplete = (result) => {
    if (result.success) {
      // If moveToNext is true, automatically generate a new challenge
      if (result.moveToNext) {
        generateNewChallenge();
      }
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={generateNewChallenge}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      {challenge && (
        <>
          <CipherChallenge
            title="Atbash Cipher Challenge"
            description="In an Atbash cipher, each letter in the alphabet is mapped to its reverse counterpart (A ↔ Z, B ↔ Y, C ↔ X, etc.). This creates a simple but effective way to encode messages!"
            encodedMessage={challenge.encodedMessage}
            correctAnswer={challenge.correctAnswer}
            originalText={challenge.originalText}
            hint={challenge.hint}
            difficulty={challenge.difficulty}
            onComplete={handleComplete}
          />
        </>
      )}
    </Container>
  );
};

export default AtbashCipher; 